import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { fmtOdds } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShareFlexCard } from "../components/ShareFlexCard";
import {
  CalendarDays, Star, Wind, Thermometer, MapPin, Heart, MessageCircle, ArrowRight, Check, Flame, Fish,
} from "lucide-react";

const GOLD = "#a855f7";
const GREEN = "#22c55e";

function Panel({ title, action, actionTo, children, className = "", testId }) {
  return (
    <div className={`card-surface rounded-xl p-5 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base font-bold uppercase tracking-wide text-white">{title}</h3>
        {action && (
          <Link to={actionTo} className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#a855f7] hover:text-white transition-colors duration-200">
            {action} <ArrowRight size={11} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function Radial({ pct, label }) {
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={GREEN} strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={`${pct} 100`} pathLength="100" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-data text-lg font-bold text-[#22c55e]">{pct}%</div>
    </div>
  );
}

function americanToDecimal(a) {
  return 1 + (a > 0 ? a / 100 : 100 / Math.abs(a));
}

function EVMini() {
  const [odds, setOdds] = useState("+310");
  const [prob, setProb] = useState(22);
  const [stake, setStake] = useState(1);
  const a = parseInt(String(odds).replace("+", ""), 10);
  const valid = !isNaN(a) && a !== 0 && prob > 0 && prob < 100 && stake > 0;
  let ev = 0, kelly = 0, implied = 0;
  if (valid) {
    const dec = americanToDecimal(a);
    const p = prob / 100;
    const b = dec - 1;
    ev = stake * b * p - stake * (1 - p);
    kelly = Math.max((b * p - (1 - p)) / b, 0);
    implied = (1 / dec) * 100;
  }
  const input = "w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-2 py-1.5 text-sm font-data outline-none transition-colors duration-200";
  return (
    <Panel title="EV Calculator" action="Full Tool" actionTo="/ev" testId="widget-ev">
      <div className="grid grid-cols-3 gap-2">
        <div><div className="text-[10px] uppercase text-neutral-500 mb-1">Odds</div><input value={odds} onChange={(e) => setOdds(e.target.value)} data-testid="mini-ev-odds" className={input} /></div>
        <div><div className="text-[10px] uppercase text-neutral-500 mb-1">Win %</div><input type="number" value={prob} onChange={(e) => setProb(Number(e.target.value))} data-testid="mini-ev-prob" className={input} /></div>
        <div><div className="text-[10px] uppercase text-neutral-500 mb-1">Stake (u)</div><input type="number" value={stake} onChange={(e) => setStake(Number(e.target.value))} data-testid="mini-ev-stake" className={input} /></div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 border-t border-white/10 pt-3 text-center">
        <div><div className="text-[10px] uppercase text-neutral-500">Implied</div><div className="font-data font-bold text-[#22c55e]">{valid ? implied.toFixed(1) : "—"}%</div></div>
        <div><div className="text-[10px] uppercase text-neutral-500">EV</div><div className={`font-data font-bold ${ev >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{valid ? `${ev >= 0 ? "+" : ""}${ev.toFixed(3)}` : "—"}</div></div>
        <div><div className="text-[10px] uppercase text-neutral-500">Kelly</div><div className="font-data font-bold text-[#a855f7]">{valid ? (kelly * 100).toFixed(1) : "—"}%</div></div>
      </div>
    </Panel>
  );
}

const CATS = [
  { cat: "lock", label: "LOCKS", stars: "★★★★★", color: "#a855f7" },
  { cat: "value", label: "VALUE", stars: "★★★★", color: "#a855f7" },
  { cat: "longshot", label: "LONG SHOT", stars: "★", color: "#22c55e" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [showFlex, setShowFlex] = useState(false);
  const { data: board } = useQuery({ queryKey: ["board"], queryFn: () => api.get("/board").then((r) => r.data) });
  const { data: fishStreak } = useQuery({ queryKey: ["fishStreak"], queryFn: () => api.get("/fish-streak").then((r) => r.data) });
  const { data: nflBoard } = useQuery({ queryKey: ["nfl", "board"], queryFn: () => api.get("/nfl/board").then((r) => r.data), staleTime: 30 * 60 * 1000 });
  const nflWeek = (nflBoard?.days || []).flatMap((d) => d.games).slice(0, 4);
  const { data: tracker } = useQuery({ queryKey: ["tracker"], queryFn: () => api.get("/hit-tracker").then((r) => r.data) });
  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: feed } = useQuery({ queryKey: ["feed", "main", "all"], queryFn: () => api.get("/community/posts?channel=main").then((r) => r.data) });

  const players = playersData?.players || [];
  const featuredId = players[0]?.mlb_id;
  const { data: featured } = useQuery({
    queryKey: ["player", String(featuredId)],
    queryFn: () => api.get(`/players/${featuredId}`).then((r) => r.data),
    enabled: !!featuredId,
  });
  const fp = featured && !featured.locked ? featured.player : null;

  const picks = board?.picks || [];
  const fishPicks = picks.filter((p) => p.category === "fish");
  const todayW = tracker?.today?.wins || 0;
  const todayL = tracker?.today?.losses || 0;
  const todayTotal = todayW + todayL;
  const radialPct = todayTotal > 0 ? Math.round((todayW / todayTotal) * 100) : Math.round(tracker?.win_pct || 0);
  const posts = (feed?.posts || []).slice(0, 3);
  const dateStr = board?.date
    ? new Date(board.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="grid grid-cols-12 gap-4">
      {(fishPicks.length > 0 || fishStreak?.last) && (
        <div className="col-span-12 card-surface fish-card border rounded-xl p-4 flex flex-wrap items-center gap-4 fade-up" data-testid="widget-fish">
          <div className="w-11 h-11 rounded-full bg-[#38bdf8]/15 border border-[#38bdf8]/50 flex items-center justify-center shrink-0 twinkle">
            <Fish size={20} className="text-[#38bdf8]" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#38bdf8] font-bold">Fish of the Day — free for every member</span>
              {fishStreak?.streak >= 2 && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/50 px-2 py-0.5 rounded-full glow-pulse" data-testid="fish-streak-badge">
                  <Flame size={11} /> Fish cashed {fishStreak.streak} days straight
                </span>
              )}
              {fishStreak?.streak === 1 && fishStreak?.last && (
                <span className="text-[10px] uppercase tracking-widest font-bold bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/40 px-2 py-0.5 rounded-full" data-testid="fish-last-result">
                  Yesterday's fish cashed
                </span>
              )}
              {fishStreak?.streak === 0 && fishStreak?.last && (
                <span className="text-[10px] uppercase tracking-widest font-bold bg-white/5 text-neutral-400 border border-white/15 px-2 py-0.5 rounded-full" data-testid="fish-last-result">
                  Last fish: {fishStreak.last.player_name} — chalked
                </span>
              )}
            </div>
            {fishPicks.map((f) => (
              <div key={f.id} className="font-chalk text-2xl text-white leading-8" data-testid={`fish-pick-${f.id}`}>
                {f.player_name} <span className="text-[#38bdf8]">{f.bet}</span> <span className="font-data text-sm text-neutral-400">{fmtOdds(f.odds)}</span>
                {f.result === "win" && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold bg-[#22c55e]/20 text-[#22c55e] px-2 py-0.5 rounded align-middle">Cashed</span>}
              </div>
            ))}
            {fishPicks.length === 0 && (
              <div className="font-chalk text-2xl text-neutral-400 leading-8" data-testid="fish-pending">Today's fish drops soon — check back.</div>
            )}
            {(fishStreak?.history || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="fish-history">
                {fishStreak.history.slice(0, 6).map((h, i) => (
                  <span key={i} data-testid={`fish-history-${i}`}
                    className={`text-[10px] font-data px-2 py-0.5 rounded-full border ${h.result === "win" ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" : "border-red-500/40 text-red-400 bg-red-500/10"}`}>
                    {h.date?.slice(5)} · {h.player_name} {h.bet} {h.odds ? `(${h.odds > 0 ? "+" : ""}${h.odds})` : ""} — {h.result === "win" ? "W" : "L"}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Link to="/board" data-testid="fish-see-board" className="btn-shine text-black text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded-full">Full Board</Link>
          {fishStreak?.streak >= 2 && (
            <button onClick={() => setShowFlex(true)} data-testid="fish-flex-btn"
              className="text-[11px] font-black uppercase tracking-wider border border-[#38bdf8]/50 text-[#38bdf8] hover:bg-[#38bdf8]/10 px-4 py-2 rounded-full transition-colors duration-200">
              Flex It
            </button>
          )}
        </div>
      )}
      {nflWeek.length > 0 && (
        <div className="card-surface rounded-xl p-4 fade-up-1" data-testid="dash-nfl-week">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-sm font-black uppercase tracking-widest text-neutral-300">This Week in the NFL <span className="text-neutral-600">· {nflBoard?.season}</span></h3>
            <Link to="/nfl" data-testid="dash-nfl-link" className="text-[11px] font-black uppercase tracking-wider text-[#a855f7] hover:underline">Full NFL Sheet →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {nflWeek.map((g, i) => (
              <Link to="/nfl" key={g.id} className="bg-black/40 border border-white/10 hover:border-[#a855f7]/50 rounded-lg p-2.5 transition-colors duration-200" data-testid={`dash-nfl-game-${i}`}>
                <div className="text-xs font-bold flex items-center gap-1.5">
                  {g.away.logo && <img src={g.away.logo} alt="" className="w-4 h-4 object-contain" />}{g.away.abbrev}
                  <span className="text-neutral-600">@</span>
                  {g.home.logo && <img src={g.home.logo} alt="" className="w-4 h-4 object-contain" />}{g.home.abbrev}
                </div>
                <div className="text-[10px] text-neutral-500 font-data mt-1">{g.day_label?.split(",")[0]} {g.time_et}{g.lines?.spread_home != null ? ` · ${g.home.abbrev} ${g.lines.spread_home > 0 ? "+" : ""}${g.lines.spread_home}` : ""}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
      {showFlex && (
        <ShareFlexCard
          record={{ wins: tracker?.record?.wins || 0, losses: tracker?.record?.losses || 0, units: tracker?.units ?? 0 }}
          fishStreak={fishStreak}
          onClose={() => setShowFlex(false)}
        />
      )}
      {/* Row 1: Chalkboard + Hero + Hit Tracker */}
      <div className="col-span-12 lg:col-span-4 chalk-texture border-2 border-white/15 rounded-xl p-5 fade-up" data-testid="widget-board">
        <div className="flex items-center justify-between border-b border-white/15 pb-2">
          <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
            <CalendarDays size={15} className="text-[#a855f7]" /> Today's Board
          </div>
          <span className="text-xs text-neutral-400">{dateStr}</span>
        </div>
        <div className="mt-3 space-y-4">
          {CATS.map(({ cat, label, stars, color }) => {
            const rows = picks.filter((p) => p.category === cat);
            if (rows.length === 0) return null;
            return (
              <div key={cat}>
                <div className="font-chalk text-2xl" style={{ color }}>
                  <span className="tracking-tight">{stars}</span> <span className="ml-1">{label}</span>
                </div>
                {rows.slice(0, 3).map((p) => (
                  <Link to={`/research/${p.mlb_id}`} key={p.id} className="block font-chalk text-[22px] text-white leading-7 hover:text-[#a855f7] transition-colors duration-200">
                    {p.player_name} <span className="text-neutral-400 text-lg">{p.bet.includes("HR") ? "HR" : p.bet}</span>
                    {cat === "longshot" && <span className="text-[#22c55e] text-lg ml-1">({fmtOdds(p.odds)})</span>}
                  </Link>
                ))}
                <div className="border-b border-white/15 mt-2" />
              </div>
            );
          })}
          {board?.locked_count > 0 && (
            <Link to="/settings" className="block font-chalk text-xl text-neutral-400 hover:text-[#a855f7] transition-colors duration-200" data-testid="dashboard-locked-picks">
              + {board.locked_count} more locked — go Pro
            </Link>
          )}
        </div>
      </div>

      <div className="col-span-12 lg:col-span-5 relative overflow-hidden rounded-xl border border-white/10 fade-up-1 min-h-[280px]" data-testid="widget-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/f5264b37-fb76-4ad8-969a-59110049862c/images/1bd6c3e31b1bdba9d1654658e81a156169d42e100af715ae6a2693adb809dd2f.jpeg)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
        <div className="relative p-7 flex flex-col justify-center h-full">
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase leading-[0.95] tracking-tight">
            Research.<br />Analyze.<br />Execute.<br /><span className="gold-text">Cash.</span>
          </h2>
          <p className="text-neutral-300 text-sm mt-3">Every sport. One sheet. <b className="text-white">Pooh Sheet Wins.</b></p>
          <div className="flex gap-2 mt-4">
            <Link to="/board" data-testid="hero-view-board" className="bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase text-xs tracking-wider px-4 py-2.5 rounded transition-colors duration-200">Full Board</Link>
            <Link to="/research" data-testid="hero-view-research" className="border border-white/25 hover:border-[#a855f7] text-white font-bold uppercase text-xs tracking-wider px-4 py-2.5 rounded transition-colors duration-200">Research</Link>
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-3 fade-up-2">
        <Panel title="Hit Tracker" testId="widget-hit-tracker" className="h-full">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase text-neutral-500">Today's Record</div>
              <div className="font-data text-3xl font-bold"><span className="text-[#22c55e]">{todayW}</span> <span className="text-neutral-500">/ {todayTotal}</span></div>
              {tracker?.streak?.type === "W" && tracker.streak.count >= 2 && (
                <div className="mt-1.5 inline-flex items-center gap-1 border border-[#a855f7]/50 bg-[#a855f7]/10 text-[#a855f7] text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" data-testid="dashboard-streak-badge">
                  <Flame size={10} /> {tracker.streak.count} Win Streak
                </div>
              )}
            </div>
            <Radial pct={radialPct} />
          </div>
          <div className="mt-4 space-y-2.5 text-sm border-t border-white/10 pt-3">
            <div className="flex justify-between"><span className="text-neutral-400">ROI</span><span className={`font-data font-bold ${(tracker?.roi || 0) >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{(tracker?.roi || 0) > 0 ? "+" : ""}{tracker?.roi ?? 0}%</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Units</span><span className={`font-data font-bold ${(tracker?.units || 0) >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{(tracker?.units || 0) > 0 ? "+" : ""}{tracker?.units ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Month to Date</span><span className="font-data text-white">{tracker?.mtd?.wins ?? 0}-{tracker?.mtd?.losses ?? 0} <span className="text-[#22c55e]">{(tracker?.mtd?.units || 0) > 0 ? "+" : ""}{tracker?.mtd?.units ?? 0}u</span></span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Home Runs Hit</span><span className="font-data font-bold text-[#a855f7]">{tracker?.hrs_hit ?? 0}</span></div>
          </div>
          <Link to="/hit-tracker" data-testid="hit-tracker-full-btn"
            className="mt-4 block text-center border border-[#a855f7]/50 text-[#a855f7] hover:bg-[#a855f7] hover:text-black font-bold uppercase text-xs tracking-wider py-2 rounded transition-colors duration-200">
            View Full Tracker
          </Link>
        </Panel>
      </div>

      {/* Row 2: Research Center + Odds Comparison + Confidence Meter */}
      <div className="col-span-12 lg:col-span-6 fade-up-2">
        <Panel title="Research Center" action="View Full Profile" actionTo={fp ? `/research/${fp.mlb_id}` : "/research"} testId="widget-research" className="h-full">
          {fp ? (
            <>
              <div className="flex flex-wrap items-center gap-4">
                <img src={fp.headshot} alt={fp.name} className="w-14 h-14 rounded-full object-cover bg-white/5 border-2 border-[#a855f7]/40" />
                <div className="flex-1 min-w-[140px]">
                  <div className="font-display text-xl font-bold uppercase">{fp.name}</div>
                  <div className="text-xs text-neutral-500">{fp.team} | {fp.position}</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-lg px-4 py-2 text-center">
                  <div className="text-[10px] uppercase text-neutral-500">HR Probability</div>
                  <div className="font-data text-2xl font-bold text-[#22c55e]">{fp.stats.hr_probability}%</div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {[["Barrel %", fp.stats.barrel_pct], ["Hard Hit %", fp.stats.hard_hit_pct], ["Fly Ball %", fp.stats.fly_ball_pct], ["Pull %", fp.stats.pull_pct]].map(([l, v]) => (
                  <div key={l} className="bg-black/40 border border-white/10 rounded p-2.5 text-center">
                    <div className="text-[10px] uppercase text-neutral-500">{l}</div>
                    <div className="font-data text-lg font-bold">{v}%</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-center">
                <div className="bg-black/40 border border-white/10 rounded p-2.5">
                  <div className="text-[10px] uppercase text-neutral-500">Pitcher Matchup</div>
                  <div className="font-data text-sm font-bold mt-1">{fp.matchup.hand}HP <span className={fp.matchup.hr9 >= 1.4 ? "text-[#22c55e]" : "text-neutral-400"}>({fp.matchup.hr9} HR/9)</span></div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5">
                  <div className="text-[10px] uppercase text-neutral-500">Weather</div>
                  <div className="font-data text-sm font-bold mt-1 flex items-center justify-center gap-1"><Thermometer size={12} className="text-[#a855f7]" />{fp.weather.temp}°F {fp.weather.conditions}</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5">
                  <div className="text-[10px] uppercase text-neutral-500">Wind</div>
                  <div className="font-data text-sm font-bold mt-1 flex items-center justify-center gap-1"><Wind size={12} className="text-[#a855f7]" />{fp.weather.wind_speed} mph <span className="text-neutral-400 text-xs">{fp.weather.wind_dir}</span></div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5">
                  <div className="text-[10px] uppercase text-neutral-500">Ballpark Rating</div>
                  <div className={`font-data text-sm font-bold mt-1 ${fp.park.rating >= 7 ? "text-[#22c55e]" : "text-neutral-300"}`}>{fp.park.rating}/10 <MapPin size={11} className="inline text-[#a855f7]" /></div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">Last 15 Games</div><div className="font-data text-sm font-bold mt-1">.{String(fp.splits.last15.avg).split(".")[1]} / {fp.splits.last15.hr} HR</div></div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">Last 30 Games</div><div className="font-data text-sm font-bold mt-1">.{String(fp.splits.last30.avg).split(".")[1]} / {fp.splits.last30.hr} HR</div></div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">vs Pitcher (Career)</div><div className="font-data text-sm font-bold mt-1">.{String(fp.splits.vs_pitcher.avg).split(".")[1]} / {fp.splits.vs_pitcher.hr} HR</div></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">Vegas Odds</div><div className="font-data text-lg font-bold">{fmtOdds(fp.odds.best_odds)}</div></div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">Implied Probability</div><div className="font-data text-lg font-bold text-[#22c55e]">{fp.odds.best_implied}%</div></div>
                <div className="bg-black/40 border border-white/10 rounded p-2.5"><div className="text-[10px] uppercase text-neutral-500">Value Edge</div><div className={`font-data text-lg font-bold ${fp.odds.edge > 0 ? "text-[#22c55e]" : "text-red-400"}`}>{fp.odds.edge > 0 ? "+" : ""}{fp.odds.edge}%</div></div>
              </div>
            </>
          ) : (
            <div className="text-sm text-neutral-500">
              Full research profiles are a <span className="text-[#a855f7] font-semibold">Pro</span> feature.{" "}
              <Link to="/settings" className="text-[#a855f7] underline hover:text-white transition-colors duration-200">Upgrade to unlock</Link>.
            </div>
          )}
        </Panel>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3">
        <Panel title="Odds Comparison" action="Tracker" actionTo="/odds" testId="widget-odds" className="h-full">
          {fp ? (
            <>
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-neutral-500 border-b border-white/10 pb-2">
                <span>Sportsbook</span><span>{fp.name.split(" ").slice(-1)[0]} HR</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {fp.odds.lines.map((l) => (
                  <div key={l.book} className={`flex justify-between items-center px-2.5 py-1.5 rounded text-sm ${l.book === fp.odds.best_book ? "border border-[#a855f7]/60 bg-[#a855f7]/10" : "border border-transparent"}`}>
                    <span className={l.book === fp.odds.best_book ? "text-[#a855f7] font-semibold" : "text-neutral-300"}>{l.book}</span>
                    <span className="font-data font-bold flex items-center gap-1">{fmtOdds(l.odds)}{l.book === fp.odds.best_book && <Star size={11} fill={GOLD} className="text-[#a855f7]" />}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-neutral-400">Best Line</span><span className="font-data font-bold text-[#22c55e]">{fmtOdds(fp.odds.best_odds)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Value Difference</span><span className={`font-data font-bold ${fp.odds.edge > 0 ? "text-[#22c55e]" : "text-red-400"}`}>{fp.odds.edge > 0 ? "+" : ""}{fp.odds.edge}%</span></div>
              </div>
            </>
          ) : (
            <div className="text-sm text-neutral-500">Odds comparison is a <span className="text-[#a855f7] font-semibold">Pro</span> feature. <Link to="/settings" className="text-[#a855f7] underline">Upgrade</Link>.</div>
          )}
        </Panel>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3">
        <Panel title="Likely To HR" action="View All" actionTo="/likely-hr" testId="widget-confidence" className="h-full">
          <div className="space-y-3">
            {players.slice(0, 6).map((p) => (
              <Link to={`/research/${p.mlb_id}`} key={p.mlb_id} className="flex items-center gap-3 group">
                <span className="text-sm text-neutral-200 group-hover:text-[#a855f7] w-28 truncate transition-colors duration-200">{p.name}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#a855f7]" style={{ width: `${p.confidence * 10}%` }} />
                </div>
                <span className="font-data text-xs font-bold text-white w-7 text-right">{Number(p.confidence).toFixed(1)}</span>
              </Link>
            ))}
          </div>
        </Panel>
      </div>

      {/* Row 3: EV mini + Parlay mini + Community + Plans */}
      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3"><EVMini /></div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3">
        <Panel title="Parlay Builder" action="Open Builder" actionTo="/parlay" testId="widget-parlay" className="h-full">
          {(() => {
            const legs = picks.filter((p) => p.mlb_id).slice(0, 3);
            if (legs.length === 0) return <div className="text-sm text-neutral-500">No picks yet today.</div>;
            const dec = legs.reduce((acc, l) => acc * americanToDecimal(l.odds), 1);
            const prob = legs.reduce((acc, l) => acc * Math.min(l.confidence / 10, 0.6), 1);
            const am = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
            return (
              <>
                <div className="space-y-1.5">
                  {legs.map((l) => (
                    <div key={l.id} className="flex justify-between items-center text-sm border border-white/10 rounded px-2.5 py-1.5">
                      <span className="flex items-center gap-2 truncate"><Check size={13} className="text-[#a855f7] shrink-0" />{l.player_name} <span className="text-neutral-600 text-xs">{l.bet.includes("HR") ? "HR" : l.bet}</span></span>
                      <span className="font-data text-neutral-300">{fmtOdds(l.odds)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-neutral-400">Parlay Odds</span><span className="font-data font-bold text-[#a855f7]">{fmtOdds(am)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Win Probability</span><span className="font-data">{(prob * 100).toFixed(2)}%</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Payout (1 Unit)</span><span className="font-data text-[#22c55e] font-bold">{dec.toFixed(2)} Units</span></div>
                </div>
                <Link to="/parlay" data-testid="parlay-add-slip-btn"
                  className="mt-3 block text-center bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase text-xs tracking-wider py-2 rounded transition-colors duration-200">
                  Add to Slip
                </Link>
              </>
            );
          })()}
        </Panel>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3">
        <Panel title="Community Feed" action="All Posts" actionTo="/community" testId="widget-community" className="h-full">
          <div className="space-y-3">
            {posts.length === 0 && <div className="text-sm text-neutral-500">No posts yet — start the conversation.</div>}
            {posts.map((p) => (
              <div key={p.id} className="flex gap-2.5">
                <div className="w-8 h-8 rounded-full bg-[#a855f7]/20 border border-[#a855f7]/40 flex items-center justify-center font-display font-bold text-[#a855f7] text-sm shrink-0">
                  {p.author.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs"><span className="font-semibold text-[#a855f7]">{p.author.name}</span></div>
                  <p className="text-xs text-neutral-300 line-clamp-2">{p.content}</p>
                  <div className="flex gap-3 mt-1 text-[11px] text-neutral-600">
                    <span className="flex items-center gap-1"><Heart size={11} /> {p.like_count}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={11} /> {p.comment_count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-3 fade-up-3">
        <Panel title="Choose Your Plan" testId="widget-plans" className="h-full">
          <div className="space-y-2">
            {[
              { key: "free", name: "Free", price: "$0", perks: "1 Daily Pick · Basic Research" },
              { key: "pro", name: "Pooh Sheet Pro", price: "$29", perks: "Full Board · Research · EV Tools" },
              { key: "vip", name: "VIP", price: "$79", perks: "Live Updates · Alerts · VIP Channel" },
            ].map((t) => {
              const current = user?.tier === t.key;
              return (
                <div key={t.key} className={`flex items-center justify-between rounded-lg px-3 py-2.5 border ${current ? "border-[#a855f7] bg-[#a855f7]/10" : "border-white/10 bg-black/30"}`}>
                  <div>
                    <div className="text-sm font-bold text-white">{t.name} <span className="font-data text-[#a855f7]">{t.price}<span className="text-neutral-500 text-[10px]">/mo</span></span></div>
                    <div className="text-[10px] text-neutral-500">{t.perks}</div>
                  </div>
                  {current ? (
                    <span className="text-[9px] uppercase font-bold bg-[#a855f7] text-black px-2 py-1 rounded">Current</span>
                  ) : (
                    <Link to="/settings" data-testid={`plan-cta-${t.key}`} className="text-[10px] uppercase font-bold border border-[#a855f7]/50 text-[#a855f7] hover:bg-[#a855f7] hover:text-black px-2.5 py-1.5 rounded transition-colors duration-200">
                      {t.key === "free" ? "Included" : "Get Started"}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
